import { useState, useRef, useEffect } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input/input';
import { Search } from 'lucide-react';
import en from 'react-phone-number-input/locale/en';

interface CountrySelectProps {
    value?: string;
    onChange: (value: string) => void;
    labels?: Record<string, string>;
    className?: string; // react-phone-number-input passes styling/className
    options?: { value: string; label: string }[]; // It passes options if not using getCountries? But typically we gen our own or use what's passed.
    // react-phone-number-input passes these:
    // value, onChange, options, iconComponent, disabled, readOnly, etc.
}

export default function CustomCountrySelect({ value, onChange, labels, className, ...props }: CountrySelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Generate country options if not provided (though PhoneInput might pass them)
    // But typically for custom UI we might want full control.
    // react-phone-number-input passes `options` prop which is [{ value: 'RU', label: 'Russia' }, ...]
    // We can use that.

    // If options are passed (which they are by PhoneInput), utilize them.
    const countryOptions = (props.options || []).map((option: any) => ({
        code: option.value,
        name: option.label,
        // Add custom flag logic or whatever needed
    }));

    // Find selected option
    const selectedOption = countryOptions.find((c: any) => c.code === value);

    const filteredOptions = countryOptions.filter((c: any) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => {
        if (!isOpen) setSearchQuery("");
    }, [isOpen]);

    return (
        <div className={`relative ${className || ''}`} ref={containerRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 h-full px-3 py-2 bg-transparent border-0 focus:outline-none"
            >
                {selectedOption ? (
                    <img
                        src={`https://flagcdn.com/w40/${selectedOption.code.toLowerCase()}.png`}
                        alt={selectedOption.name}
                        className="w-6 h-4 object-cover rounded-sm border border-gray-100"
                    />
                ) : (
                    <GlobeIcon />
                )}
                <span className="text-gray-500 text-[10px] scale-75">▼</span>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-[300px] max-h-60 overflow-hidden bg-white border border-gray-200 rounded-md shadow-lg z-50 flex flex-col">
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 text-gray-700 bg-gray-50"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div className="overflow-y-auto flex-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((country: any) => (
                                <button
                                    key={country.code}
                                    type="button"
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 ${country.code === value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                        }`}
                                    onClick={() => {
                                        onChange(country.code);
                                        setIsOpen(false);
                                    }}
                                >
                                    <img
                                        src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                                        alt={country.name}
                                        className="w-6 h-4 object-cover rounded-sm flex-shrink-0"
                                    />
                                    <span className="truncate">{country.name}</span>
                                    <span className="text-gray-400 text-xs ml-auto">
                                        +{getCountryCallingCode(country.code)}
                                    </span>
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                No countries found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function GlobeIcon() {
    return (
        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    );
}
